import { Suspense, useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router";
import { HR_EMPLOYEES_PAGE_ROUTE } from "@/shared/utils";
import { Loader } from "@/shared/components";
import { useAuth } from "@/shared/hooks";
import { AuthProvider } from "../auth";
import PrivateLayout from "../layout/PrivateLayout";
import PublicLayout from "../layout/PublicLayout";
import { ErrorBoundary } from "./ErrorBoundary";
import { lazyPage } from "./lazyPage";
import PermissionProtectedRoute from "./PermissionProtectedRoute";
import PublicRoute from "./PublicRoute";
import { privateRoutes, publicRoutes } from "./routes";

const HomePage = lazyPage(() => import("@/features/home").then((m) => ({ default: m.HomePage })), ["HomePage"]);
const PmDashboardPage = lazyPage(
  () => import("@/features/projects").then((m) => ({ default: m.PmDashboardPage })),
  ["PmDashboardPage"],
);
const HrDashboardPage = lazyPage(
  () => import("@/features/hr/employees").then((m) => ({ default: m.HrDashboardPage })),
  ["HrDashboardPage"],
);
const AccountantDashboardPage = lazyPage(
  () => import("@/features/accounting").then((m) => ({ default: m.AccountantDashboardPage })),
  ["AccountantDashboardPage"],
);
const ContractorHomePage = lazyPage(
  () => import("@/features/home").then((m) => ({ default: m.ContractorHomePage })),
  ["ContractorHomePage", "EmployeePortal"],
);
const VolunteerHomePage = lazyPage(
  () => import("@/features/home").then((m) => ({ default: m.VolunteerHomePage })),
  ["VolunteerHomePage"],
);
const WorkerHomePage = lazyPage(
  () => import("@/features/home").then((m) => ({ default: m.WorkerHomePage })),
  ["WorkerHomePage"],
);
const LawyerDashboardPage = lazyPage(
  () => import("@/features/legal").then((m) => ({ default: m.LawyerDashboardPage })),
  ["LawyerDashboardPage"],
);
const AdminDashboardPage = lazyPage(
  () => import("@/features/admin").then((m) => ({ default: m.AdminDashboardPage })),
  ["AdminDashboardPage"],
);

function IndexRoute() {
  const { user, hasRole, hasAnyRole } = useAuth();

  if (hasRole("chief")) {
    return <HomePage />;
  }

  if (hasRole("pm")) {
    return <PmDashboardPage />;
  }

  if (hasRole("hr_manager") || (user?.user_type === "expert" && hasRole("hr"))) {
    return <HrDashboardPage />;
  }

  if (hasRole("accountant")) {
    return <AccountantDashboardPage />;
  }

  if (hasRole("lawyer")) {
    return <LawyerDashboardPage />;
  }

  if (hasRole("admin")) {
    return <AdminDashboardPage />;
  }

  if (hasAnyRole(["contractor", "msb_contractor"])) {
    return <ContractorHomePage />;
  }

  if (hasRole("volunteer")) {
    return <VolunteerHomePage />;
  }

  return <WorkerHomePage />;
}

export function AppRouter() {
  const location = useLocation();

  useEffect(function scrollToTopOnRouteChange() {
    if (location.hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname, location.hash]);

  return (
    <ErrorBoundary resetKey={location.pathname}>
      <Suspense fallback={<Loader isFullPage />}>
        <Routes>
          <Route element={<PublicLayout />}>
            {publicRoutes.filter((route) => route.enabled !== false).map((route) => (
              <Route
                key={route.path}
                path={route.path}
                element={
                  <PublicRoute alwaysAccessible={route.alwaysAccessible}>
                    <route.element />
                  </PublicRoute>
                }
              />
            ))}
          </Route>

          <Route
            path="/"
            element={
              <AuthProvider>
                <PermissionProtectedRoute>
                  <PrivateLayout />
                </PermissionProtectedRoute>
              </AuthProvider>
            }>
            <Route index element={<PermissionProtectedRoute><IndexRoute /></PermissionProtectedRoute>} />
            {privateRoutes.filter((route) => route.enabled !== false).map((route) => (
              <Route
                key={route.path}
                path={route.path}
                element={
                  <PermissionProtectedRoute
                    requiredPermissions={route.requiredPermissions}
                    requiredRoles={route.requiredRoles}
                    requiredFeature={route.requiredFeature}
                    alwaysAccessible={route.alwaysAccessible}
                  >
                    <route.element />
                  </PermissionProtectedRoute>
                }
              />
            ))}
            <Route path="/hr/volunteers" element={<Navigate to="/projects/volunteers" replace />} />
            <Route path="*" element={<Navigate to={HR_EMPLOYEES_PAGE_ROUTE} replace />} />
          </Route>
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

